import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import TextScaleToggle from './TextScaleToggle';
import RefreshButton from './RefreshButton';
import { fs } from '../utils/typography';
import { requestInstall, useInstallPrompt } from '../utils/installPrompt';

export const RAIL_WIDTH = 60;

// Left navigation rail for a phone held sideways (see useCompactLandscape).
// Landscape phones have ~340px of usable height, so the portrait layout's
// stacked header + view switcher + banner would leave almost nothing for the
// calendar. The rail holds the same controls down the left edge instead:
// brand mark, view buttons, Filter, and a "More" menu with the disclaimer,
// text size, theme, refresh, and site links.
//
// `views` / `filter` are the same button descriptors Home's portrait view
// switcher uses ({ key, icon, label, ariaLabel, onClick, active, highlight, title }).
const LandscapeRail = ({ views, filter, onShowLegal }) => {
    const { theme, themeName, toggleTheme } = useTheme();
    const tokens = theme.tokens;
    const c = tokens.colors;
    const [menuOpen, setMenuOpen] = useState(false);
    const { canInstall } = useInstallPrompt();
    const menuRef = useRef(null);
    const menuButtonRef = useRef(null);

    // Close the menu on an outside tap or Escape.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onPointerDown = (e) => {
            if (menuRef.current?.contains(e.target) || menuButtonRef.current?.contains(e.target)) return;
            setMenuOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
        document.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    const railButton = ({ key, icon, label, ariaLabel, onClick, active, highlight, title, buttonRef, expanded }) => (
        <button
            key={key}
            ref={buttonRef}
            type="button"
            onClick={onClick}
            className={`flex flex-col items-center justify-center rounded-lg transition ${active ? theme.viewButtonActive : theme.viewButtonInactive}`}
            style={{
                width: 48, height: 42, gap: 3, flex: '0 0 auto',
                ...(highlight && !active ? { color: c.accent, boxShadow: `inset 0 0 0 1px ${c.accent}` } : {}),
            }}
            aria-label={ariaLabel || label}
            title={title || ariaLabel || label}
            aria-pressed={expanded === undefined ? active : undefined}
            aria-expanded={expanded}
        >
            <i className={`fas ${icon}`} style={{ fontSize: fs(13) }} aria-hidden="true" />
            <span style={{ fontFamily: tokens.fonts.ui, fontSize: fs(9.5), fontWeight: 600, lineHeight: 1 }}>{label}</span>
        </button>
    );

    const menuLink = { fontFamily: tokens.fonts.ui, fontSize: fs(13.5), fontWeight: 500, color: c.ink, padding: '6px 0', display: 'block' };

    return (
        <nav
            aria-label="Calendar navigation"
            className="flex-none flex flex-col items-center h-full"
            style={{
                // Notch/safe-area padding is on the app root (.App in index.css).
                width: RAIL_WIDTH,
                background: c.surface, borderRight: `1px solid ${c.gridLine}`,
                position: 'relative', zIndex: 30,
            }}
        >
            <div className="flex flex-col items-center w-full h-full overflow-y-auto" style={{ padding: '6px 0', gap: 4 }}>
                <Link to="/" aria-label="ATLTech.events home" className="flex-none flex items-center justify-center" style={{ height: 34, marginBottom: 2 }}>
                    <img src="favicon-32x32.png" alt="" className="h-7 w-7 object-contain" />
                </Link>

                {views.map(railButton)}
                {filter && railButton(filter)}

                <div style={{ marginTop: 'auto' }} />
                {railButton({
                    key: 'more', icon: menuOpen ? 'fa-times' : 'fa-ellipsis-h', label: 'More', ariaLabel: 'More options',
                    onClick: () => setMenuOpen((open) => !open), active: menuOpen, buttonRef: menuButtonRef, expanded: menuOpen,
                })}
            </div>

            {menuOpen && (
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label="More options"
                    className="rounded-xl"
                    style={{
                        position: 'absolute', left: '100%', bottom: 8, marginLeft: 8, width: 260,
                        maxHeight: 'calc(100% - 16px)', overflowY: 'auto',
                        background: c.surface, border: `1px solid ${c.gridLine}`,
                        boxShadow: `0 8px 28px ${tokens.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.18)'}`,
                        padding: '12px 14px', textAlign: 'left',
                    }}
                >
                    <p style={{ fontFamily: tokens.fonts.ui, fontSize: fs(12), color: c.muted, lineHeight: 1.4, margin: 0 }}>
                        <i className="fas fa-info-circle mr-1.5" style={{ color: c.accent }} aria-hidden="true" />
                        Events are subject to change. Please verify with the event host.
                    </p>

                    <div className="flex items-center gap-2" style={{ margin: '12px 0', paddingTop: 12, borderTop: `1px solid ${c.gridLine}` }}>
                        <TextScaleToggle compact />
                        <button
                            type="button"
                            onClick={toggleTheme}
                            className={`rounded-lg h-8 w-8 flex items-center justify-center ${theme.viewButtonInactive}`}
                            aria-label={themeName === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                            title={themeName === 'dark' ? 'Light mode' : 'Dark mode'}
                        >
                            <i className={`fas fa-${themeName === 'dark' ? 'sun' : 'moon'} text-sm`} aria-hidden="true" />
                        </button>
                        <RefreshButton className={`rounded-lg h-8 w-8 flex items-center justify-center ${theme.viewButtonInactive}`} />
                    </div>

                    <div style={{ paddingTop: 6, borderTop: `1px solid ${c.gridLine}` }}>
                        <Link to="/about" style={menuLink}>About</Link>
                        <Link to="/community" style={menuLink}>Community</Link>
                        <Link to="/subscribe" style={menuLink}>Subscribe</Link>
                        {canInstall && (
                            <button type="button" onClick={() => { setMenuOpen(false); requestInstall(); }} style={{ ...menuLink, background: 'transparent', border: 'none', textAlign: 'left', width: '100%' }}>
                                <i className="fas fa-mobile-screen-button mr-2" style={{ color: c.accent }} aria-hidden="true" />Add to Home Screen
                            </button>
                        )}
                        {onShowLegal && (
                            <button type="button" onClick={() => { setMenuOpen(false); onShowLegal(); }} style={{ ...menuLink, background: 'transparent', border: 'none', textAlign: 'left', width: '100%' }}>
                                Legal
                            </button>
                        )}
                    </div>

                    <div style={{ fontFamily: tokens.fonts.ui, fontSize: fs(11), color: c.faint, marginTop: 8 }}>
                        Brought to you by <a target="_blank" rel="noopener noreferrer" href="https://www.mirability.com" style={{ color: c.muted, fontWeight: 600 }}>Mirability<sup>SM</sup></a>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default LandscapeRail;
